import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { assetService } from '../services/assetService';
import { ArrowLeft, Upload, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const MigrateImagesPage = () => {
  const navigate = useNavigate();
  const [migrationStatus, setMigrationStatus] = useState('idle'); // idle, running, completed, error
  const [assets, setAssets] = useState([]);
  const [currentAsset, setCurrentAsset] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [logs, setLogs] = useState([]);
  const [migratedCount, setMigratedCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);

  const CLOUDINARY_CLOUD_NAME = 'dkallcbko';
  const CLOUDINARY_UPLOAD_PRESET = 'luxury_assets_unsigned';
  const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

  const folderMapping = {
    'vehicle': 'The Haute Circle/Vehicles',
    'jet': 'The Haute Circle/Private Jets',
    'yacht': 'The Haute Circle/Yachts',
    'property': 'The Haute Circle/Vacation Homes'
  };

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { message, type, timestamp }]);
  };

  const uploadImageToCloudinary = async (imageUrl, assetCategory, imageCategory, assetName) => {
    const baseFolder = folderMapping[assetCategory] || 'The Haute Circle/Other';
    const fullFolder = `${baseFolder}/${imageCategory}`;

    try {
      // Check if it's already a Cloudinary URL
      if (imageUrl.includes('cloudinary.com')) {
        addLog(`Image already on Cloudinary: ${imageUrl.substring(0, 50)}...`, 'success');
        return imageUrl;
      }

      // Check if it's a base64 image
      if (imageUrl.startsWith('data:image')) {
        // Convert base64 to blob
        const response = await fetch(imageUrl);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append('file', blob);
        formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
        formData.append('folder', fullFolder);
        formData.append('tags', `${imageCategory},luxury_asset,${assetCategory},migrated`);

        const uploadResponse = await fetch(CLOUDINARY_UPLOAD_URL, {
          method: 'POST',
          body: formData
        });

        if (!uploadResponse.ok) {
          throw new Error(`Cloudinary upload failed: ${uploadResponse.statusText}`);
        }

        const result = await uploadResponse.json();
        addLog(`✓ Uploaded base64 image to: ${fullFolder}`, 'success');
        return result.secure_url;
      }

      // If it's an external URL, upload it
      const formData = new FormData();
      formData.append('file', imageUrl);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', fullFolder);
      formData.append('tags', `${imageCategory},luxury_asset,${assetCategory},migrated`);

      const uploadResponse = await fetch(CLOUDINARY_UPLOAD_URL, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error(`Cloudinary upload failed: ${uploadResponse.statusText}`);
      }

      const result = await uploadResponse.json();
      addLog(`✓ Uploaded external image to: ${fullFolder}`, 'success');
      return result.secure_url;

    } catch (error) {
      addLog(`✗ Failed to upload image: ${error.message}`, 'error');
      throw error;
    }
  };

  const migrateAsset = async (asset) => {
    setCurrentAsset(asset);
    addLog(`\n--- Processing: ${asset.name} (${asset.category}) ---`, 'info');

    try {
      const exteriorImages = [];
      const interiorImages = [];
      let allImages = asset.images || [];

      // If asset has separate exterior/interior arrays, use those
      if (asset.exterior_images && asset.exterior_images.length > 0) {
        allImages = [...asset.exterior_images, ...(asset.interior_images || [])];
      }

      // Determine split point
      const exteriorCount = asset.exterior_count || Math.ceil(allImages.length / 2);

      addLog(`Found ${allImages.length} images (${exteriorCount} exterior, ${allImages.length - exteriorCount} interior)`, 'info');

      // Upload exterior images
      for (let i = 0; i < exteriorCount && i < allImages.length; i++) {
        const imageUrl = allImages[i];
        addLog(`Uploading exterior image ${i + 1}/${exteriorCount}...`, 'info');
        const cloudinaryUrl = await uploadImageToCloudinary(imageUrl, asset.category, 'exterior', asset.name);
        exteriorImages.push(cloudinaryUrl);
      }

      // Upload interior images
      for (let i = exteriorCount; i < allImages.length; i++) {
        const imageUrl = allImages[i];
        addLog(`Uploading interior image ${i - exteriorCount + 1}/${allImages.length - exteriorCount}...`, 'info');
        const cloudinaryUrl = await uploadImageToCloudinary(imageUrl, asset.category, 'interior', asset.name);
        interiorImages.push(cloudinaryUrl);
      }

      // Update asset in backend
      const updatedAsset = {
        ...asset,
        exterior_images: exteriorImages,
        interior_images: interiorImages,
        images: [...exteriorImages, ...interiorImages]
      };

      await assetService.updateAsset(asset.id, updatedAsset);
      addLog(`✓ Asset updated successfully with ${exteriorImages.length + interiorImages.length} Cloudinary URLs`, 'success');
      
      setMigratedCount(prev => prev + 1);
      return true;

    } catch (error) {
      addLog(`✗ Error migrating asset: ${error.message}`, 'error');
      setErrorCount(prev => prev + 1);
      return false;
    }
  };

  const startMigration = async () => {
    setMigrationStatus('running');
    setLogs([]);
    setMigratedCount(0);
    setErrorCount(0);
    
    addLog('=== Starting Image Migration to Cloudinary ===', 'info');
    addLog(`Cloud Name: ${CLOUDINARY_CLOUD_NAME}`, 'info');
    addLog(`Upload Preset: ${CLOUDINARY_UPLOAD_PRESET}`, 'info');

    try {
      // Fetch all assets
      addLog('\nFetching all assets from backend...', 'info');
      const allAssets = await assetService.getAllAssets();
      setAssets(allAssets);
      setProgress({ current: 0, total: allAssets.length });
      addLog(`Found ${allAssets.length} assets to process`, 'success');

      // Filter assets that have images
      const assetsWithImages = allAssets.filter(asset => 
        asset.images && asset.images.length > 0
      );

      if (assetsWithImages.length === 0) {
        addLog('No assets with images found!', 'error');
        setMigrationStatus('completed');
        return;
      }

      addLog(`\nProcessing ${assetsWithImages.length} assets with images...\n`, 'info');

      // Migrate each asset
      for (let i = 0; i < assetsWithImages.length; i++) {
        setProgress({ current: i + 1, total: assetsWithImages.length });
        await migrateAsset(assetsWithImages[i]);
        
        // Small delay between assets to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      addLog('\n=== Migration Complete ===', 'success');
      addLog(`✓ Successfully migrated: ${migratedCount} assets`, 'success');
      if (errorCount > 0) {
        addLog(`✗ Failed: ${errorCount} assets`, 'error');
      }
      setMigrationStatus('completed');

    } catch (error) {
      addLog(`\n✗ Migration failed: ${error.message}`, 'error');
      setMigrationStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} className="mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Migrate Images to Cloudinary</h1>
        </div>

        {/* Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload size={20} />
              Migration Tool
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-gray-600">
                This tool will migrate all existing asset images to Cloudinary with the proper folder structure:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-gray-600">
                <li><strong>Vehicles:</strong> The Haute Circle/Vehicles/exterior & interior</li>
                <li><strong>Private Jets:</strong> The Haute Circle/Private Jets/exterior & interior</li>
                <li><strong>Yachts:</strong> The Haute Circle/Yachts/exterior & interior</li>
                <li><strong>Vacation Homes:</strong> The Haute Circle/Vacation Homes/exterior & interior</li>
              </ul>
              <div className="flex items-start gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded">
                <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <strong>Important:</strong> This process will:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Upload all existing images to Cloudinary</li>
                    <li>Update asset records with new Cloudinary URLs</li>
                    <li>Replace base64 and external URLs with Cloudinary URLs</li>
                    <li>This process cannot be undone automatically</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Control Panel */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Migration Status</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {migrationStatus === 'idle' && 'Ready to start migration'}
                  {migrationStatus === 'running' && `Processing asset ${progress.current} of ${progress.total}`}
                  {migrationStatus === 'completed' && 'Migration completed successfully'}
                  {migrationStatus === 'error' && 'Migration encountered errors'}
                </p>
              </div>
              <Button
                onClick={startMigration}
                disabled={migrationStatus === 'running'}
                className={`${
                  migrationStatus === 'running' 
                    ? 'bg-gray-400' 
                    : 'bg-black hover:bg-gray-800'
                } text-white`}
              >
                {migrationStatus === 'running' ? (
                  <>
                    <Loader size={16} className="mr-2 animate-spin" />
                    Migrating...
                  </>
                ) : (
                  <>
                    <Upload size={16} className="mr-2" />
                    Start Migration
                  </>
                )}
              </Button>
            </div>

            {/* Progress Bar */}
            {migrationStatus === 'running' && (
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-2">
                  <span>Progress</span>
                  <span>{progress.current} / {progress.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-black h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Summary */}
            {(migrationStatus === 'completed' || migrationStatus === 'error') && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded text-center">
                  <div className="text-2xl font-bold text-blue-600">{progress.total}</div>
                  <div className="text-sm text-blue-800">Total Assets</div>
                </div>
                <div className="p-4 bg-green-50 rounded text-center">
                  <div className="text-2xl font-bold text-green-600">{migratedCount}</div>
                  <div className="text-sm text-green-800">Migrated</div>
                </div>
                <div className="p-4 bg-red-50 rounded text-center">
                  <div className="text-2xl font-bold text-red-600">{errorCount}</div>
                  <div className="text-sm text-red-800">Failed</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Current Asset */}
        {currentAsset && migrationStatus === 'running' && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Loader size={24} className="animate-spin text-blue-600" />
                <div>
                  <h3 className="font-semibold">{currentAsset.name}</h3>
                  <p className="text-sm text-gray-600">
                    Category: {currentAsset.category} | Images: {currentAsset.images?.length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logs */}
        <Card>
          <CardHeader>
            <CardTitle>Migration Logs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-900 text-gray-100 p-4 rounded font-mono text-sm h-96 overflow-y-auto">
              {logs.length === 0 ? (
                <p className="text-gray-500">No logs yet. Click "Start Migration" to begin.</p>
              ) : (
                logs.map((log, index) => (
                  <div
                    key={index}
                    className={`mb-1 ${
                      log.type === 'error' ? 'text-red-400' :
                      log.type === 'success' ? 'text-green-400' :
                      'text-gray-300'
                    }`}
                  >
                    <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MigrateImagesPage;
